import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./ParticipantsGrid.css";
import { buildOverflowItems, isOverflowTileItem } from "./overflowItems";

/**
 * Seviye olcerler icin TEK AudioContext. Karo basina context acmak Chromium'un
 * sayfa basina context limitine takiliyordu (kalabalik odada gostergeler olu
 * kaliyor). Context hic kapatilmaz; karolar yalnizca kendi source dugumunu
 * cozer. Cikisa BAGLANMAZ → uzak ses ikinci kez calinmaz, AEC bozulmaz.
 */
let sharedAudioContext: AudioContext | null = null;
function getSharedAudioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const Ctx: typeof AudioContext | undefined =
    (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!Ctx) return null;
  if (!sharedAudioContext || sharedAudioContext.state === "closed") {
    try {
      sharedAudioContext = new Ctx();
    } catch {
      return null;
    }
  }
  return sharedAudioContext;
}


// Seçili hoparlör (audiooutput) deviceId — her ParticipantTile'a prop threading
// yapmadan setSinkId için tek noktadan sağlanır.
const AudioOutputContext = createContext<string | undefined>(undefined);

export interface Participant {
  id: string;
  name: string;
  userId?: string;
  /** Ham LiveKit identity — split (cam/screen) tile'larda id suffix'li olur; mute
   *  hedeflemesi için orijinal identity gerekir. */
  identity?: string;
  stream: MediaStream | null;
  isAudioEnabled: boolean;
  isVideoEnabled: boolean;
  isHandRaised?: boolean;
  /** [MEMEET-HANDORDER] el kaldirma sirasi (1 = ilk kaldiran). */
  handRaiseOrder?: number;
  avatarUrl?: string;
  isLocal?: boolean;
  isSpeaking?: boolean;
  isScreenSharing?: boolean;
}

// Host (oda sahibi) rozeti + moderatör mute kontrolü — tüm ParticipantTile
// çağrı noktalarına prop geçmeden tek noktadan sağlanır.
interface HostControlsValue {
  /** Oda sahibinin lodos user id'si (createdBy.id) */
  hostUserId?: string;
  /** Yerel kullanıcı host mu — remote tile'larda mute butonu yalnız o zaman görünür */
  canMuteParticipants?: boolean;
  /** muted=true sustur, muted=false yeniden ac (host yetkisi backend'de dogrulanir). */
  onMuteParticipant?: (participant: Participant, muted: boolean) => void | Promise<void>;
  muteLabel?: string;
  unmuteLabel?: string;
  hostBadgeLabel?: string;
}
const HostControlsContext = createContext<HostControlsValue>({});

// Yerel (yalnız bu kullanıcıyı etkileyen) katılımcı ses ayarı + hoparlörü kapat (2026-10-14).
// Ses öğelerine (audio + sesli video) uygulanır; tercih hesapta saklanır ve tüm MeMeet'lerde aynıdır.
export interface VolumeControlsValue {
  get: (identity: string) => { v: number; m: 0 | 1 };
  set: (identity: string, patch: { v?: number; m?: 0 | 1 }) => void;
  /** Hoparlör kapalı: tüm uzak sesler (ve SoundPad) susar */
  deafened: boolean;
  labels: { volume: string; muteForMe: string; unmuteForMe: string; onlyForYou: string };
}
const DEFAULT_VOLUME: VolumeControlsValue = {
  get: () => ({ v: 1, m: 0 }),
  set: () => undefined,
  deafened: false,
  labels: { volume: "Volume", muteForMe: "Mute for me", unmuteForMe: "Unmute for me", onlyForYou: "Only you hear this change" },
};
const VolumeControlsContext = createContext<VolumeControlsValue>(DEFAULT_VOLUME);
// Ses tercihi Lodos kullanıcısına bağlanır (identity cihaz başınadır, her girişte değişir) — mobil ile aynı anahtar.
const participantKey = (p: Participant) => (p.userId ? `u:${p.userId}` : String(p.identity || p.id || ""));
/** Uzak ses öğesine yerel ses seviyesini uygula */
function applyLocalVolume(el: HTMLMediaElement | null, vc: VolumeControlsValue, key: string) {
  if (!el) return;
  const pv = vc.get(key);
  const silent = vc.deafened || pv.m === 1;
  try { el.volume = silent ? 0 : Math.max(0, Math.min(1, pv.v)); } catch { /* yok */ }
}

interface ParticipantsGridProps {
  participants: Participant[];
  activeSpeakerId?: string | null;
  layout?: "auto" | "grid" | "spotlight";
  onParticipantClick?: (participant: Participant) => void;
  participantActionLabel?: string;
  onParticipantActionClick?: (participant: Participant) => void | Promise<void>;
  handRaisedLabel?: string;
  pinLabel?: string;
  unpinLabel?: string;
  getOverflowLabel?: (count: number) => string;
  /** Seçili hoparlör (audiooutput) deviceId — remote media element'lerine setSinkId ile uygulanır */
  audioOutputDeviceId?: string;
  /** Oda sahibinin lodos user id'si — eşleşen tile'a "Yönetici" rozeti basılır */
  hostUserId?: string;
  /** Yerel kullanıcı host ise remote katılımcılar için mute butonu göster */
  canMuteParticipants?: boolean;
  /** Host bir remote katılımcının mikrofonunu kapatmak/açmak istediğinde çağrılır (muted bayrağı) */
  onMuteParticipant?: (participant: Participant, muted: boolean) => void | Promise<void>;
  /** Mute butonu etiketi (i18n) */
  muteLabel?: string;
  /** Sessizdeki katılımcı için "mikrofonu aç" etiketi (i18n) */
  unmuteLabel?: string;
  /** Host rozeti etiketi (i18n) */
  hostBadgeLabel?: string;
  /** Yerel katılımcı ses ayarları + hoparlör kapalı */
  volumeControls?: VolumeControlsValue;
}

const MAX_VISIBLE_TILES = 9;

export function ParticipantsGrid(props: ParticipantsGridProps) {
  const hostControls = useMemo<HostControlsValue>(
    () => ({
      hostUserId: props.hostUserId,
      canMuteParticipants: props.canMuteParticipants,
      onMuteParticipant: props.onMuteParticipant,
      muteLabel: props.muteLabel,
      unmuteLabel: props.unmuteLabel,
      hostBadgeLabel: props.hostBadgeLabel,
    }),
    [props.hostUserId, props.canMuteParticipants, props.onMuteParticipant, props.muteLabel, props.unmuteLabel, props.hostBadgeLabel]
  );
  return (
    <AudioOutputContext.Provider value={props.audioOutputDeviceId}>
      <HostControlsContext.Provider value={hostControls}>
        <VolumeControlsContext.Provider value={props.volumeControls || DEFAULT_VOLUME}>
          <ParticipantsGridInner {...props} />
        </VolumeControlsContext.Provider>
      </HostControlsContext.Provider>
    </AudioOutputContext.Provider>
  );
}

function ParticipantsGridInner({
  participants,
  activeSpeakerId,
  layout = "auto",
  onParticipantClick,
  participantActionLabel,
  onParticipantActionClick,
  handRaisedLabel,
  pinLabel,
  unpinLabel,
  getOverflowLabel,
}: ParticipantsGridProps) {
  const [actionMenuParticipantId, setActionMenuParticipantId] = useState<string | null>(null);
  const [pinnedParticipantId, setPinnedParticipantId] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!actionMenuParticipantId) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (gridRef.current?.contains(target)) return;
      setActionMenuParticipantId(null);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [actionMenuParticipantId]);

  useEffect(() => {
    if (!pinnedParticipantId) return;
    if (participants.some((p) => p.id === pinnedParticipantId)) return;
    setPinnedParticipantId(null);
  }, [participants, pinnedParticipantId]);

  const handleParticipantActionClick = useCallback(
    async (participant: Participant) => {
      if (!onParticipantActionClick) return;
      try {
        await onParticipantActionClick(participant);
      } finally {
        setActionMenuParticipantId(null);
      }
    },
    [onParticipantActionClick]
  );

  const handleTogglePin = useCallback((participantId: string) => {
    setPinnedParticipantId((prev) => (prev === participantId ? null : participantId));
  }, []);

  const handleParticipantTileClick = useCallback(
    (participant: Participant) => {
      onParticipantClick?.(participant);
      if (!onParticipantActionClick || !participantActionLabel || participant.isLocal || !participant.userId) return;
      setActionMenuParticipantId((prev) => (prev === participant.id ? null : participant.id));
    },
    [onParticipantClick, onParticipantActionClick, participantActionLabel]
  );

  const sortedParticipants = useMemo(() => {
    const list = [...participants];
    list.sort((a, b) => {
      // Local user always last (bottom-right in grid/strip)
      if (a.isLocal) return 1;
      if (b.isLocal) return -1;

      // Screen sharers first
      const aSharing = Boolean(a.isScreenSharing);
      const bSharing = Boolean(b.isScreenSharing);
      if (aSharing !== bSharing) return aSharing ? -1 : 1;

      // Camera-on participants next
      const aVideo = Boolean(a.isVideoEnabled);
      const bVideo = Boolean(b.isVideoEnabled);
      if (aVideo !== bVideo) return aVideo ? -1 : 1;

      // Active speaker next
      if (activeSpeakerId) {
        if (a.id === activeSpeakerId) return -1;
        if (b.id === activeSpeakerId) return 1;
      }

      return 0;
    });
    return list;
  }, [participants, activeSpeakerId]);

  const getGridClass = useCallback((count: number) => {
    if (count === 1) return "grid-1";
    if (count === 2) return "grid-2";
    if (count <= 4) return "grid-4";
    if (count <= 6) return "grid-6";
    if (count <= 9) return "grid-9";
    return "grid-many";
  }, []);

  const pinnedParticipant = useMemo(() => {
    if (!pinnedParticipantId) return null;
    return participants.find((p) => p.id === pinnedParticipantId) || null;
  }, [participants, pinnedParticipantId]);

  const screenSharers = useMemo(() => participants.filter((p) => p.isScreenSharing), [participants]);
  const hasCameraOn = useMemo(() => participants.some((p) => p.isVideoEnabled), [participants]);
  const hasCameraOff = useMemo(() => participants.some((p) => !p.isVideoEnabled), [participants]);

  const shouldUseMeetSpotlight = useMemo(() => {
    if (pinnedParticipant) return participants.length > 1;
    if (screenSharers.length > 0) return participants.length > screenSharers.length;
    if (layout !== "auto") return false;
    return hasCameraOn && hasCameraOff;
  }, [pinnedParticipant, participants.length, screenSharers.length, layout, hasCameraOn, hasCameraOff]);

  const renderMeetSpotlight = useCallback(
    (opts: { main: Participant[]; sidebar: Participant[] }) => {
      const sidebarSlots = Math.max(MAX_VISIBLE_TILES - opts.main.length, 0);
      const sidebarItems = buildOverflowItems(opts.sidebar, sidebarSlots, "overflow-sidebar", { alwaysIncludeLocal: true });

      const mainGridClass =
        opts.main.length <= 1 ? "spotlight-main-grid-1" : opts.main.length === 2 ? "spotlight-main-grid-2" : "spotlight-main-grid-4";

      const resolveOverflowLabel = (count: number) => getOverflowLabel?.(count) ?? `${count}`;

      return (
        <div className="participants-grid grid-spotlight" ref={gridRef}>
          <div className={`spotlight-main ${mainGridClass}`}>
            {opts.main.map((participant) => (
              <ParticipantTile
                key={participant.id}
                participant={participant}
                isActiveSpeaker={participant.id === activeSpeakerId}
                isPinned={participant.id === pinnedParticipantId}
                pinLabel={pinLabel}
                unpinLabel={unpinLabel}
                onTogglePin={() => handleTogglePin(participant.id)}
                handRaisedLabel={handRaisedLabel}
                showActionMenu={
                  Boolean(onParticipantActionClick && participantActionLabel && participant.userId && !participant.isLocal) &&
                  actionMenuParticipantId === participant.id
                }
                actionLabel={participantActionLabel}
                onActionClick={() => handleParticipantActionClick(participant)}
                onClick={() => handleParticipantTileClick(participant)}
              />
            ))}
          </div>

          <div className="spotlight-sidebar">
            {sidebarItems.map((item) =>
              isOverflowTileItem(item) ? (
                <OverflowTile key={item.id} participants={item.participants} label={resolveOverflowLabel(item.participants.length)} />
              ) : (
                <ParticipantTile
                  key={item.id}
                  participant={item}
                  isActiveSpeaker={item.id === activeSpeakerId}
                  isPinned={item.id === pinnedParticipantId}
                  pinLabel={pinLabel}
                  unpinLabel={unpinLabel}
                  onTogglePin={() => handleTogglePin(item.id)}
                  handRaisedLabel={handRaisedLabel}
                  showActionMenu={
                    Boolean(onParticipantActionClick && participantActionLabel && item.userId && !item.isLocal) &&
                    actionMenuParticipantId === item.id
                  }
                  actionLabel={participantActionLabel}
                  onActionClick={() => handleParticipantActionClick(item)}
                  onClick={() => handleParticipantTileClick(item)}
                />
              )
            )}
          </div>
        </div>
      );
    },
    [
      actionMenuParticipantId,
      activeSpeakerId,
      getOverflowLabel,
      handleParticipantActionClick,
      handleParticipantTileClick,
      handleTogglePin,
      onParticipantActionClick,
      participantActionLabel,
      pinnedParticipantId,
      pinLabel,
      unpinLabel,
      handRaisedLabel,
    ]
  );

  if (pinnedParticipant && participants.length > 1) {
    const sidebar = sortedParticipants.filter((p) => p.id !== pinnedParticipant.id);
    return renderMeetSpotlight({ main: [pinnedParticipant], sidebar });
  }

  if (screenSharers.length > 0 && participants.length > screenSharers.length) {
    const orderedSharers = [...screenSharers].sort((a, b) => {
      if (a.isLocal) return -1;
      if (b.isLocal) return 1;
      if (activeSpeakerId) {
        if (a.id === activeSpeakerId) return -1;
        if (b.id === activeSpeakerId) return 1;
      }
      return 0;
    });
    const main = orderedSharers.slice(0, 2);
    const mainIds = new Set(main.map((p) => p.id));
    const sidebar = sortedParticipants.filter((p) => !mainIds.has(p.id));
    return renderMeetSpotlight({ main, sidebar });
  }

  if (shouldUseMeetSpotlight) {
    const cameraOn = sortedParticipants.filter((p) => p.isVideoEnabled);
    const main = cameraOn.slice(0, Math.min(4, cameraOn.length));
    const mainIds = new Set(main.map((p) => p.id));
    const sidebar = sortedParticipants.filter((p) => !mainIds.has(p.id));
    return renderMeetSpotlight({ main, sidebar });
  }

  if (layout === "spotlight" && activeSpeakerId) {
    const speaker = participants.find((p) => p.id === activeSpeakerId);
    const others = participants.filter((p) => p.id !== activeSpeakerId);

    return (
      <div className="participants-grid grid-spotlight" ref={gridRef}>
        {speaker && <div className="spotlight-main spotlight-main-grid-1">
          <ParticipantTile
            participant={speaker}
            isActiveSpeaker={true}
            isPinned={speaker.id === pinnedParticipantId}
            pinLabel={pinLabel}
            unpinLabel={unpinLabel}
            onTogglePin={() => handleTogglePin(speaker.id)}
            handRaisedLabel={handRaisedLabel}
            showActionMenu={
              Boolean(onParticipantActionClick && participantActionLabel && speaker.userId && !speaker.isLocal) &&
              actionMenuParticipantId === speaker.id
            }
            actionLabel={participantActionLabel}
            onActionClick={() => handleParticipantActionClick(speaker)}
            onClick={() => handleParticipantTileClick(speaker)}
          />
        </div>}
        <div className="spotlight-sidebar">
          {others.map((participant) => (
            <ParticipantTile
              key={participant.id}
              participant={participant}
              isActiveSpeaker={false}
              isPinned={participant.id === pinnedParticipantId}
              pinLabel={pinLabel}
              unpinLabel={unpinLabel}
              onTogglePin={() => handleTogglePin(participant.id)}
              handRaisedLabel={handRaisedLabel}
              showActionMenu={
                Boolean(onParticipantActionClick && participantActionLabel && participant.userId && !participant.isLocal) &&
                actionMenuParticipantId === participant.id
              }
              actionLabel={participantActionLabel}
              onActionClick={() => handleParticipantActionClick(participant)}
              onClick={() => handleParticipantTileClick(participant)}
            />
          ))}
        </div>
      </div>
    );
  }

  const resolveOverflowLabel = (count: number) => getOverflowLabel?.(count) ?? `${count}`;
  const gridItems = buildOverflowItems(sortedParticipants, MAX_VISIBLE_TILES, "overflow-grid", { alwaysIncludeLocal: true });

  return (
    <div className={`participants-grid ${getGridClass(gridItems.length)}`} ref={gridRef}>
      {gridItems.map((item) =>
        isOverflowTileItem(item) ? (
          <OverflowTile key={item.id} participants={item.participants} label={resolveOverflowLabel(item.participants.length)} />
        ) : (
          <ParticipantTile
            key={item.id}
            participant={item}
            isActiveSpeaker={item.id === activeSpeakerId}
            isPinned={item.id === pinnedParticipantId}
            pinLabel={pinLabel}
            unpinLabel={unpinLabel}
            onTogglePin={() => handleTogglePin(item.id)}
            handRaisedLabel={handRaisedLabel}
            showActionMenu={
              Boolean(onParticipantActionClick && participantActionLabel && item.userId && !item.isLocal) &&
              actionMenuParticipantId === item.id
            }
            actionLabel={participantActionLabel}
            onActionClick={() => handleParticipantActionClick(item)}
            onClick={() => handleParticipantTileClick(item)}
          />
        )
      )}
    </div>
  );
}

interface ParticipantTileProps {
  participant: Participant;
  isActiveSpeaker: boolean;
  onClick?: () => void;
  showActionMenu?: boolean;
  actionLabel?: string;
  onActionClick?: () => void;
  handRaisedLabel?: string;
  onTogglePin?: () => void;
  isPinned?: boolean;
  pinLabel?: string;
  unpinLabel?: string;
}

function ParticipantTile({
  participant,
  isActiveSpeaker,
  onClick,
  showActionMenu,
  actionLabel,
  onActionClick,
  handRaisedLabel,
  onTogglePin,
  isPinned,
  pinLabel = "Full screen",
  unpinLabel = "Exit full screen",
}: ParticipantTileProps) {
  const hostControls = useContext(HostControlsContext);
  const isHost = Boolean(
    hostControls.hostUserId &&
      participant.userId &&
      String(participant.userId) === String(hostControls.hostUserId)
  );
  // Host hem susturabilir hem geri acabilir → kosulda "sesi acik olmali" YOK.
  const canMuteThisParticipant = Boolean(
    hostControls.canMuteParticipants && hostControls.onMuteParticipant && !participant.isLocal && !isHost
  );
  const videoRef = useRef<HTMLVideoElement>(null);
  const volumeControls = useContext(VolumeControlsContext);
  const volKey = participantKey(participant);
  const localVol = volumeControls.get(volKey);
  const [volumeOpen, setVolumeOpen] = useState(false);
  const volumeBoxRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => { applyLocalVolume(videoRef.current, volumeControls, volKey); }, [volumeControls, volKey, localVol.v, localVol.m]);
  useEffect(() => {
    if (!volumeOpen) return;
    const onDown = (e: PointerEvent) => { if (volumeBoxRef.current && !volumeBoxRef.current.contains(e.target as Node)) setVolumeOpen(false); };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [volumeOpen]);
  // Seviye cubuklari React state'i DEGIL (2026-09-14): her rAF karesinde
  // setState cagirmak karo basina saniyede 60 render demekti. Seviye artik
  // dogrudan DOM'a bir CSS degiskeni olarak yaziliyor; React yalnizca
  // "konusuyor mu" esigi degisince yeniden render ediyor.
  const [localSpeaking, setLocalSpeaking] = useState(false);
  const localSpeakingRef = useRef(false);
  const indicatorRef = useRef<HTMLDivElement | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const smoothedLevelRef = useRef(0);
  const animationFrameRef = useRef<number | null>(null);
  const hasVideoTrack = !!participant.stream?.getVideoTracks()?.length;
  const showVideo = !!participant.stream && participant.isVideoEnabled && hasVideoTrack;
  const [isShimmering, setIsShimmering] = useState(false);
  const shimmerTimerRef = useRef<number | null>(null);
  const nameBadgeRef = useRef<HTMLSpanElement>(null);
  const nameMeasureRef = useRef<HTMLSpanElement>(null);

  const formatNameForChip = useCallback((fullName: string) => {
    const parts = String(fullName || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (parts.length <= 1) return String(fullName || "").trim();
    const firstName = parts[0];
    const lastName = parts[parts.length - 1];
    const lastInitial = lastName ? `${lastName.charAt(0).toUpperCase()}.` : "";
    return `${firstName} ${lastInitial}`.trim();
  }, []);

  const fullDisplayName = String(participant.name || "").trim();
  const shortDisplayName = useMemo(() => formatNameForChip(fullDisplayName), [formatNameForChip, fullDisplayName]);
  const [displayName, setDisplayName] = useState(fullDisplayName);

  useEffect(() => {
    setDisplayName(fullDisplayName);
  }, [fullDisplayName]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    if (!participant.stream) {
      el.srcObject = null;
      return;
    }

    el.srcObject = participant.stream;
    el.play().catch(() => {
      // ignore autoplay errors
    });
  }, [participant.stream, participant.isVideoEnabled]);

  useEffect(() => {
    if (!participant.stream || !participant.isAudioEnabled) {
      smoothedLevelRef.current = 0;
      localSpeakingRef.current = false;
      setLocalSpeaking(false);
      indicatorRef.current?.style.setProperty("--memeet-audio-level", "0%");
      return;
    }

    const audioTrack = participant.stream.getAudioTracks()[0];
    if (!audioTrack) return;

    // TEK paylasilan AudioContext (2026-09-14): eskiden HER karo icin ayri bir
    // AudioContext aciliyordu. Chromium sayfa basina ~6 context ile sinirli →
    // kalabalik odada sonrakiler hata verip seviye cubugu olu kaliyordu; her
    // context ayrica kendi yeniden orneklemesini yapip ses hattina yuk biniyordu.
    const ctx = getSharedAudioContext();
    if (!ctx) return;
    if (ctx.state === "suspended") void ctx.resume().catch(() => undefined);
    const source = ctx.createMediaStreamSource(participant.stream);
    sourceRef.current = source;
    analyserRef.current = ctx.createAnalyser();
    analyserRef.current.fftSize = 256;
    source.connect(analyserRef.current);

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

    const updateLevel = () => {
      if (!analyserRef.current) return;

      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
      const raw = Math.min(100, (average / 255) * 100);
      // Hizli yukselis, yavas dusus: tek hecelik seslerde cubuklar zipzip inip
      // cikmasin diye dusus yumusatiliyor. Yumusatma JS'te yapildigi icin
      // CSS transition YOK (transition her karede yeniden baslayip hedefe hic
      // ulasamiyordu — Nova ekolayzerinde ayni tuzak yasanmisti).
      const prev = smoothedLevelRef.current;
      const next = raw > prev ? raw : prev + (raw - prev) * 0.12;
      smoothedLevelRef.current = next;

      const el = indicatorRef.current;
      if (el) {
        el.style.setProperty("--memeet-audio-level", `${next.toFixed(1)}%`);
        const quiet = next <= 6;
        if (el.classList.contains("quiet") !== quiet) {
          el.classList.toggle("quiet", quiet);
        }
      }

      // Histerezis: 18 uzerinde "konusuyor", 10 altinda susuyor. Tek esikte
      // sinirda gidip gelen seviye cerceveyi titretiyordu.
      const wasSpeaking = localSpeakingRef.current;
      const nowSpeaking = wasSpeaking ? next > 10 : next > 18;
      if (nowSpeaking !== wasSpeaking) {
        localSpeakingRef.current = nowSpeaking;
        setLocalSpeaking(nowSpeaking);
      }

      animationFrameRef.current = requestAnimationFrame(updateLevel);
    };

    updateLevel();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      // Paylasilan context KAPATILMAZ; yalnizca bu karonun dugumleri cozulur.
      try {
        sourceRef.current?.disconnect();
      } catch {
        /* yut */
      }
      sourceRef.current = null;
      analyserRef.current = null;
    };
  }, [participant.stream, participant.isAudioEnabled]);

  useEffect(() => {
    return () => {
      if (shimmerTimerRef.current) {
        window.clearTimeout(shimmerTimerRef.current);
        shimmerTimerRef.current = null;
      }
    };
  }, []);

  useLayoutEffect(() => {
    const el = nameBadgeRef.current;
    const measureEl = nameMeasureRef.current;
    if (!el) return;
    if (!measureEl) return;

    const recompute = () => {
      // Measure overflow against the full-name width to avoid oscillation
      // (short name fits -> switch to full -> overflows -> switch back ...).
      const fullWidth = measureEl.scrollWidth;
      const available = el.clientWidth;
      const isOverflowing = fullWidth > available + 1;
      const next = isOverflowing ? shortDisplayName : fullDisplayName;
      setDisplayName((prev) => (prev === next ? prev : next));
    };

    recompute();

    const ro = new ResizeObserver(() => {
      recompute();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fullDisplayName, shortDisplayName]);

  const isSpeaking = localSpeaking || participant.isSpeaking;

  return (
    <div
      className={`participant-tile ${isShimmering ? "shimmer" : ""} ${isActiveSpeaker ? "active-speaker" : ""} ${isSpeaking ? "speaking" : ""}`}
      onClick={onClick}
      onMouseEnter={() => {
        if (participant.stream === null) return;
        if (participant.isScreenSharing && !showVideo) return;
        if (shimmerTimerRef.current) return;
        setIsShimmering(true);
        shimmerTimerRef.current = window.setTimeout(() => {
          shimmerTimerRef.current = null;
          setIsShimmering(false);
        }, 1150);
      }}
    >
      {participant.stream ? (
        <>
          <ParticipantAudio participant={participant} suppressed={showVideo && !participant.isScreenSharing} />
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={participant.isLocal || !participant.isAudioEnabled || !showVideo || participant.isScreenSharing || localVol.m === 1 || volumeControls.deafened}
            className={`participant-video ${participant.isScreenSharing ? "screen-share" : ""} ${participant.isLocal && !participant.isScreenSharing ? "mirrored" : ""} ${showVideo ? "" : "hidden"}`}
          />
          {!showVideo && (
            <div className="participant-avatar overlay">
              <div className="avatar-circle">
                {participant.avatarUrl ? (
                  <img className="avatar-image" src={participant.avatarUrl} alt={participant.name} />
                ) : (
                  <span className="avatar-initials">{getInitials(participant.name)}</span>
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="participant-avatar">
          <div className="avatar-circle">
            {participant.avatarUrl ? (
              <img className="avatar-image" src={participant.avatarUrl} alt={participant.name} />
            ) : (
              <span className="avatar-initials">{getInitials(participant.name)}</span>
            )}
          </div>
        </div>
      )}

      <div className="participant-bottom-overlay">
        <div className="participant-name-group">
          {isHost && (
            <span
              className="participant-host-badge"
              title={hostControls.hostBadgeLabel}
              aria-label={hostControls.hostBadgeLabel}
            >
              <HostBadgeIcon />
            </span>
          )}
          <span ref={nameBadgeRef} className="participant-name-badge" title={fullDisplayName}>
            {displayName}
            <span ref={nameMeasureRef} className="participant-name-measure" aria-hidden="true">
              {fullDisplayName}
            </span>
          </span>
        </div>
        <div className="participant-indicators">
          {!participant.isLocal && (
            <div className="participant-volume" ref={volumeBoxRef} onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className={`indicator volume-action ${localVol.m || localVol.v < 1 ? "changed" : ""}`}
                title={volumeControls.labels.volume}
                aria-label={volumeControls.labels.volume}
                aria-expanded={volumeOpen}
                onClick={(e) => { e.stopPropagation(); setVolumeOpen((o) => !o); }}
              >
                {localVol.m || volumeControls.deafened ? <SpeakerOffIcon /> : <SpeakerIcon />}
              </button>
              {volumeOpen && (
                <div className="participant-volume-pop" role="dialog" aria-label={volumeControls.labels.volume}>
                  <div className="participant-volume-row">
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      value={Math.round((localVol.m ? 0 : localVol.v) * 100)}
                      aria-label={volumeControls.labels.volume}
                      onChange={(e) => {
                        const v = Number(e.target.value) / 100;
                        volumeControls.set(volKey, { v, m: v === 0 ? 1 : 0 });
                      }}
                    />
                    <span className="participant-volume-val">{localVol.m ? 0 : Math.round(localVol.v * 100)}%</span>
                  </div>
                  <button
                    type="button"
                    className="participant-volume-mute"
                    onClick={() => volumeControls.set(volKey, localVol.m ? { m: 0, v: localVol.v > 0 ? localVol.v : 1 } : { m: 1 })}
                  >
                    {localVol.m ? volumeControls.labels.unmuteForMe : volumeControls.labels.muteForMe}
                  </button>
                  <p className="participant-volume-note">{volumeControls.labels.onlyForYou}</p>
                </div>
              )}
            </div>
          )}
          {canMuteThisParticipant && hostControls.onMuteParticipant && (
            <button
              type="button"
              className={`indicator mute-action ${participant.isAudioEnabled ? "" : "unmute"}`}
              title={participant.isAudioEnabled ? hostControls.muteLabel : hostControls.unmuteLabel}
              aria-label={participant.isAudioEnabled ? hostControls.muteLabel : hostControls.unmuteLabel}
              onClick={(e) => {
                e.stopPropagation();
                void hostControls.onMuteParticipant?.(participant, participant.isAudioEnabled);
              }}
            >
              {participant.isAudioEnabled ? <MicOffIcon /> : <MicOnIcon />}
            </button>
          )}
          {participant.isHandRaised && (
            <span className="indicator hand-raised" title={handRaisedLabel} aria-label={handRaisedLabel}>
              <HandRaiseIcon />
              {participant.handRaiseOrder ? <span className="hand-order">{participant.handRaiseOrder}</span> : null}{/* [MEMEET-HANDORDER] */}
            </span>
          )}
          {!participant.isAudioEnabled && (
            <span className="indicator muted">
              <MicOffIcon />
            </span>
          )}
          {!participant.isVideoEnabled && (
            <span className="indicator camera-off">
              <VideoOffIcon />
            </span>
          )}
          {/* Gosterge mikrofon ACIKKEN HEP durur (2026-09-14). Eskiden seviye
              sifir olunca mount/unmount oluyordu; saliselik seslerde yanip sonuyor
              ve tutarsiz gorunuyordu. Sessizken sonuk ve minimum yukseklikte bekler.
              Yukseklik `--memeet-audio-level` degiskeninden gelir, rAF dogrudan yazar. */}
          {participant.isAudioEnabled && (
            <div ref={indicatorRef} className="audio-indicator quiet">
              <div className="audio-bar" />
              <div className="audio-bar bar-mid" />
              <div className="audio-bar bar-low" />
            </div>
          )}
        </div>
      </div>

      {showActionMenu && actionLabel && onActionClick && (
        <div className="participant-action-menu" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="participant-action-button"
            onClick={(e) => {
              e.stopPropagation();
              onActionClick();
            }}
          >
            {actionLabel}
          </button>
        </div>
      )}

      {onTogglePin && (
        <div className="participant-pin-overlay">
          <button
            type="button"
            className={`participant-pin-button ${isPinned ? "pinned" : ""}`}
            title={isPinned ? unpinLabel : pinLabel}
            aria-label={isPinned ? unpinLabel : pinLabel}
            onClick={(e) => {
              e.stopPropagation();
              onTogglePin();
            }}
          >
            {isPinned ? <ExitFullscreenIcon /> : <FullscreenIcon />}
          </button>
        </div>
      )}

      {isSpeaking && <div className="speaking-border" />}
    </div>
  );
}

function ParticipantAudio({
  participant,
  suppressed,
}: {
  participant: Participant;
  suppressed: boolean;
}) {
  const audioOutputDeviceId = useContext(AudioOutputContext);
  const volumeControls = useContext(VolumeControlsContext);
  const audioRef = useRef<HTMLAudioElement>(null);
  const volKey = participantKey(participant);
  const pv = volumeControls.get(volKey);
  useEffect(() => { applyLocalVolume(audioRef.current, volumeControls, volKey); }, [volumeControls, volKey, pv.v, pv.m, participant.stream, suppressed]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    const skipAudio =
      !participant.stream ||
      participant.isLocal ||
      !participant.isAudioEnabled ||
      suppressed;
    if (skipAudio) {
      el.srcObject = null;
      return;
    }

    el.srcObject = participant.stream;
    // Output device'ı uygula. Kullanıcı device settings'ten hoparlör seçtiyse
    // ona, seçmediyse ("") default device'a yönlendir. Chrome bazen device
    // değişimini (kulaklık takma/çıkarma) audio element'in routing'ine
    // yansıtmıyor; setSinkId ile her attach'te açıkça yönlendir.
    const elAny = el as any;
    if (typeof elAny.setSinkId === "function") {
      try {
        const sinkPromise: Promise<void> = elAny.setSinkId(audioOutputDeviceId || "");
        if (sinkPromise && typeof sinkPromise.catch === "function") {
          sinkPromise.catch(() => undefined);
        }
      } catch {
        // setSinkId not supported — ignore
      }
    }
    el.play().catch(() => {
      // autoplay rejected — ignore
    });
  }, [participant.stream, participant.isLocal, participant.isAudioEnabled, suppressed, audioOutputDeviceId]);

  // Re-play audio element when audio device changes (iPhone call handoff +
  // kulaklık takma/çıkarma anında output device routing'ini yenile).
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !participant.stream || participant.isLocal) return;

    const handleDeviceChange = () => {
      const elAny = el as any;
      if (typeof elAny.setSinkId === "function") {
        try {
          const p: Promise<void> = elAny.setSinkId(audioOutputDeviceId || "");
          if (p && typeof p.catch === "function") p.catch(() => {});
        } catch {}
      }
      el.play().catch(() => {});
    };

    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);
    };
  }, [participant.stream, participant.isLocal, audioOutputDeviceId]);

  if (participant.isLocal) return null;
  return <audio ref={audioRef} autoPlay />;
}

function OverflowTile({ participants, label }: { participants: Participant[]; label: string }) {
  const visibleAvatars = participants.slice(0, 4);
  const remaining = Math.max(participants.length - visibleAvatars.length, 0);

  return (
    <div className="participant-tile overflow" role="group" aria-label={label}>
      <div className="overflow-content">
        <div className="overflow-avatar-stack" aria-hidden="true">
          {visibleAvatars.map((p) => (
            <div key={p.id} className="overflow-avatar">
              {p.avatarUrl ? <img src={p.avatarUrl} alt={p.name} /> : <span>{getInitials(p.name)}</span>}
            </div>
          ))}
          {remaining > 0 && <div className="overflow-avatar overflow-more">+{remaining}</div>}
        </div>
        <div className="overflow-label">{label}</div>
      </div>
      {participants.map((p) => (
        <ParticipantAudio key={p.id} participant={p} suppressed={false} />
      ))}
    </div>
  );
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function MicOnIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="9" y="2" width="6" height="11" rx="3" />
      <path d="M5 10v2a7 7 0 0 0 14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
    </svg>
  );
}

function MicOffIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
      <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );
}

function VideoOffIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function HostBadgeIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l8 3v6c0 5-3.5 8.5-8 11-4.5-2.5-8-6-8-11V5l8-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function HandRaiseIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 13V6.5a1.5 1.5 0 0 1 3 0V12" />
      <path d="M11 12V4.5a1.5 1.5 0 0 1 3 0V12" />
      <path d="M14 12.5V6.5a1.5 1.5 0 0 1 3 0V13" />
      <path d="M17 13v-3a1.5 1.5 0 0 1 3 0v6c0 3.31-2.69 6-6 6h-1.5a6 6 0 0 1-5.4-3.4L6 15.5c-.5-1-.2-2.2.7-2.8.8-.5 1.9-.3 2.5.5l.8 1.1" />
    </svg>
  );
}

function FullscreenIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M8 3H5a2 2 0 0 0-2 2v3" />
      <path d="M16 3h3a2 2 0 0 1 2 2v3" />
      <path d="M8 21H5a2 2 0 0 1-2-2v-3" />
      <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
    </svg>
  );
}

function ExitFullscreenIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 3H5a2 2 0 0 0-2 2v4" />
      <path d="M15 3h4a2 2 0 0 1 2 2v4" />
      <path d="M9 21H5a2 2 0 0 1-2-2v-4" />
      <path d="M15 21h4a2 2 0 0 0 2-2v-4" />
      <path d="M9 9l-3-3" />
      <path d="M15 9l3-3" />
      <path d="M9 15l-3 3" />
      <path d="M15 15l3 3" />
    </svg>
  );
}

function SpeakerIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 5 6 9H2v6h4l5 4V5z" />
      <path d="M15.5 8.5a5 5 0 0 1 0 7M19 5a10 10 0 0 1 0 14" />
    </svg>
  );
}

function SpeakerOffIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 5 6 9H2v6h4l5 4V5z" />
      <line x1="22" y1="9" x2="16" y2="15" />
      <line x1="16" y1="9" x2="22" y2="15" />
    </svg>
  );
}
